/** استخراج data از پاسخ GET /deals/create */
export const parseDealCreateResponse = (res) => res?.data ?? res ?? {};

const mapStages = (stages = []) =>
  stages
    .map((s) => ({
      id: Number(s.id),
      name: s.name,
      label_color: s.label_color || '#1890ff',
      priority: s.priority || 0,
      slug: s.slug,
      lead_pipeline_id: s.lead_pipeline_id,
    }))
    .sort((a, b) => (a.priority || 0) - (b.priority || 0));

/** نگاشت پاسخ create به گزینه‌های DealForm */
export const mapDealCreateToFilterOptions = (data = {}) => {
  const pipelineStagesMap = data.pipeline_stages || {};
  const pipelines = (data.pipelines || []).map((p) => {
    const stagesRaw = pipelineStagesMap[String(p.id)] || pipelineStagesMap[p.id] || [];
    return {
      id: p.id,
      name: p.name,
      default: p.default,
      stages: mapStages(stagesRaw),
    };
  });

  const clientsList = (data.clients || []).map((c) => ({
    id: c.id,
    name: c.name || c.name_salutation || c.client_name || `مشتری #${c.id}`,
    mobile: c.mobile || c.cell_number || c.phone || '',
  }));

  if (data.client?.id && !clientsList.some((c) => c.id === data.client.id)) {
    clientsList.unshift({
      id: data.client.id,
      name: data.client.name || `مشتری #${data.client.id}`,
      mobile: data.client.mobile || data.client.cell_number || data.client.phone || '',
    });
  }

  const leadsList = (data.leads || data.lead_contacts || []).map((l) => ({
    id: l.id,
    name: l.name || l.client_name || `سرنخ #${l.id}`,
  }));

  if (data.lead?.id && !leadsList.some((l) => l.id === data.lead.id)) {
    leadsList.unshift({
      id: data.lead.id,
      name: data.lead.name || data.lead.client_name || `سرنخ #${data.lead.id}`,
    });
  }

  return {
    pipelines: pipelines.map((p) => ({ id: p.id, name: p.name, stages: p.stages })),
    pipelinesWithStages: pipelines,
    categories: (data.categories || []).map((c) => ({
      id: c.id,
      name: c.category_name || c.name,
    })),
    products: (data.products || []).map((p) => ({ id: p.id, name: p.name })),
    owners: (data.employees || []).map((e) => ({ id: e.id, name: e.name })),
    stages: mapStages(
      pipelineStagesMap[String(data.default_pipeline_id)] ||
        pipelineStagesMap[data.default_pipeline_id] ||
        [],
    ),
    leads: leadsList,
    clients: clientsList,
    sources: (data.sources || []).map((s) => ({ id: s.id, name: s.type || s.name })),
    leadCategories: (data.lead_categories || data.categories || []).map((c) => ({
      id: c.id,
      name: c.category_name || c.name,
    })),
    clientCategories: (data.client_categories || []).map((c) => ({
      id: c.id,
      name: c.category_name || c.name,
    })),
    tags: (data.tags || []).map((t) => ({ id: Number(t.id), name: t.name })),
    myAgentId: data.my_agent_id ?? null,
  };
};

const toNumberOrUndefined = (value) => {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
};

const normalizeNoteText = (value) => {
  if (value == null) return null;
  const text = String(value).trim();
  return text !== '' ? text : null;
};

/** استخراج توضیحات سرنخ از پاسخ GET /deals/create */
export const resolveCreateFormLeadNote = (data = {}, fallback) =>
  normalizeNoteText(data?.lead?.note) ||
  normalizeNoteText(data?.lead?.description) ||
  normalizeNoteText(data?.note) ||
  normalizeNoteText(fallback) ||
  null;

/** مقادیر اولیه فرم از پاسخ create */
export const mapDealCreateToInitialValues = (data = {}, { stageIdFromUrl, userId, pipelineIdFromFilter, leadNoteFallback } = {}) => {
  const pipelineId =
    pipelineIdFromFilter != null && pipelineIdFromFilter !== ''
      ? Number(pipelineIdFromFilter)
      : data.default_pipeline_id != null
        ? Number(data.default_pipeline_id)
        : null;
  const stageId =
    stageIdFromUrl != null
      ? Number(stageIdFromUrl)
      : data.default_stage_id != null
        ? Number(data.default_stage_id)
        : data.stage?.id != null
          ? Number(data.stage.id)
          : undefined;

  const base = {
    pipeline: pipelineId != null ? Number(pipelineId) : undefined,
    stage_id: stageId,
    deal_owner: undefined,
    added_by: userId ?? undefined,
    // پیش‌فرض ایجاد معامله: ۱ ستاره
    priority: 1,
  };

  if (data.lead) {
    const lead = data.lead;
    const leadTagIds = Array.isArray(lead.tags)
      ? lead.tags.map((t) => Number(t.id)).filter((id) => !Number.isNaN(id))
      : [];
    const leadNote = resolveCreateFormLeadNote(data, leadNoteFallback);
    return {
      ...base,
      has_lead_contact: true,
      lead_contact: lead.id,
      value: toNumberOrUndefined(lead.value),
      priority: toNumberOrUndefined(lead.priority) ?? 1,
      success_rate: toNumberOrUndefined(lead.success_rate),
      source_id: lead.source?.id ?? undefined,
      tags: leadTagIds,
      deal_owner: lead.lead_owner?.id ?? userId ?? undefined,
      ...(leadNote ? { note: leadNote } : {}),
    };
  }

  if (data.client) {
    const client = data.client;
    return {
      ...base,
      has_lead_contact: true,
      lead_contact: client.id,
      client_name: client.name || undefined,
      mobile: client.mobile || undefined,
      email: client.email || undefined,
      source_id: client.source?.id ?? client.source_id ?? undefined,
      client_category_id: client.category_id ?? client.category?.id ?? undefined,
      deal_owner: client.owner_id ?? client.deal_owner ?? userId ?? undefined,
    };
  }

  const fallbackNote = resolveCreateFormLeadNote(data, leadNoteFallback);
  return fallbackNote ? { ...base, note: fallbackNote } : base;
};

export const resolveDealCreatePresetLead = (data) => {
  if (!data?.lead?.id) return null;
  return {
    id: data.lead.id,
    name: data.lead.name || data.lead.client_name || `سرنخ #${data.lead.id}`,
  };
};

export const resolveDealCreatePresetClient = (data) => {
  if (!data?.client?.id) return null;
  return {
    id: data.client.id,
    name: data.client.name || `مشتری #${data.client.id}`,
    mobile: data.client.mobile || data.client.cell_number || data.client.phone || '',
  };
};

/** ادغام تگ‌های سرنخ در کاتالوگ تگ‌های فرم (برای نمایش در Select) */
export const mergeLeadTagsIntoCatalog = (catalogTags = [], leadTags = []) => {
  const map = new Map(
    (catalogTags || []).map((t) => [Number(t.id), { id: Number(t.id), name: t.name }]),
  );
  (leadTags || []).forEach((t) => {
    if (t?.id != null) {
      map.set(Number(t.id), { id: Number(t.id), name: t.name });
    }
  });
  return Array.from(map.values());
};
